import React, { useState, useRef, useEffect, useCallback } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import ShopsMap from '../components/ShopsMap';
import ShopModal from '../components/ShopModal';
import Header from '../components/Header';
import ShopCarousel from '../components/ShopCarousel';
import './Shops.css';

// --- Fonctions utilitaires de distance ---
// Fonction pour convertir degrés en radians
const toRadians = (degrees) => {
  return degrees * Math.PI / 180;
};

// Fonction de calcul de distance Haversine
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) {
    return Infinity; // Retourne une distance infinie si les coordonnées sont manquantes
  }
  const R = 6371; // Rayon de la Terre en kilomètres
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance en km
  return distance;
};
// --- Fin Fonctions utilitaires ---

const Shops = () => {
  const [selectedShop, setSelectedShop] = useState(null);
  const [activeShopId, setActiveShopId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null); // Nouvel état pour la position user
  const [locationError, setLocationError] = useState(null); // Nouvel état pour erreur/refus
  const mapRef = useRef(null);

  // --- useEffect pour demander la géolocalisation au montage ---
  useEffect(() => {
    // Vérifier si la géolocalisation est supportée
    if (!navigator.geolocation) {
      console.log("La géolocalisation n'est pas supportée par votre navigateur.");
      setLocationError("Géolocalisation non supportée.");
      return;
    }

    // Demander la position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
        setLocationError(null); // Réinitialiser l'erreur en cas de succès
        // Le tri sera effectué dans l'autre useEffect quand les shops seront chargés
      },
      (error) => {
        console.error("Erreur de géolocalisation:", error);
        let message = "Impossible d'obtenir la position.";
        if (error.code === error.PERMISSION_DENIED) {
          message = "Vous avez refusé la géolocalisation.";
          // Optionnel: stocker le refus pour ne pas redemander ?
          // localStorage.setItem('geolocationPermissionDenied', 'true');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          message = "Position indisponible.";
        } else if (error.code === error.TIMEOUT) {
          message = "Timeout lors de la demande de position.";
        }
        setLocationError(message);
        setUserLocation(null); // S'assurer que userLocation est null en cas d'erreur
      },
      {
        enableHighAccuracy: true, // Essayer d'avoir une position plus précise
        timeout: 10000, // Temps max d'attente (10s)
        maximumAge: 0 // Forcer une position fraîche
      }
    );
  }, []); // Tableau de dépendances vide pour ne s'exécuter qu'au montage
  // --- Fin useEffect Géolocalisation ---


  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'shops'), (snapshot) => {
      let shopsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // --- Ajout du calcul de distance et tri SI userLocation est disponible ---
      if (userLocation) {
        shopsData = shopsData.map(shop => {
          // Assurez-vous que shop.coordinates existe et a latitude/longitude
          const shopLat = shop.coordinates?.latitude;
          const shopLon = shop.coordinates?.longitude;
          const distance = calculateDistance(userLocation.latitude, userLocation.longitude, shopLat, shopLon);
          return { ...shop, distance }; // Ajoute la distance à l'objet shop
        });

        // Trier les boutiques par distance croissante
        shopsData.sort((a, b) => a.distance - b.distance);
      } else {
        // Optionnel: Appliquer un tri par défaut si pas de géolocalisation
        // shopsData.sort((a, b) => a.titre.localeCompare(b.titre));
      }
      // --- Fin ajout ---

      setShops(shopsData);
      
      // Mettre à jour le shop sélectionné pour la modale si nécessaire
      if (selectedShop) {
        const updatedSelectedShop = shopsData.find(shop => shop.id === selectedShop.id);
        if (updatedSelectedShop) {
          // Récupérer la distance déjà calculée si elle existe dans shopsData
          const distance = updatedSelectedShop.distance !== undefined ? updatedSelectedShop.distance : selectedShop.distance;
          setSelectedShop({ ...updatedSelectedShop, distance });
        } else {
          // Si le shop sélectionné n'existe plus, fermer la modale et désactiver
          setSelectedShop(null);
          setActiveShopId(null);
          setIsModalOpen(false);
        }
      }
      
      setLoading(false);
    }, (error) => {
      console.error("Erreur lors de la récupération des boutiques:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  // On ajoute userLocation aux dépendances pour que le tri soit refait si la localisation arrive APRÈS les shops
  }, [selectedShop, userLocation]); 

  // Déclenché par le clic sur un marqueur de la carte (via ShopsMap)
  const handleShopSelect = useCallback((shop) => {
    setSelectedShop(shop); // Met à jour la boutique pour la modale
    setActiveShopId(shop.id); // Met à jour l'ID actif pour synchroniser le carrousel/carte
    setIsModalOpen(true); // Ouvre la modale
  }, []);

  // Déclenché par le clic sur une carte du carrousel (via ShopCarousel -> ShopCard)
  const handleCardClick = useCallback((shop) => {
    setActiveShopId(shop.id); // Met à jour l'ID actif pour synchroniser la carte
    // Optionnel: Ouvrir aussi la modale au clic sur la carte ?
    // setSelectedShop(shop); 
    // setIsModalOpen(true);
  }, []);

  // Déclenché par le changement de slide dans le carrousel (via ShopCarousel)
  const handleCarouselSlideChange = useCallback((shopId) => {
    setActiveShopId(shopId); // Met à jour l'ID actif pour synchroniser la carte
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedShop(null);
    // Optionnel: Réinitialiser activeShopId ? Ou le laisser sur le dernier shop vu ?
    // setActiveShopId(null); 
  };

  // Affichage du message d'erreur de localisation si besoin
  if (locationError && !loading) {
    // On peut afficher l'erreur de manière plus visible si on veut
    console.warn("Erreur de localisation:", locationError); // Afficher dans la console pour l'instant
    // Exemple d'affichage simple (à styliser):
    // return (
    //   <div className="shops-page-container">
    //     <p style={{ padding: '2rem', textAlign: 'center' }}>
    //       {locationError} Le tri par proximité n'est pas disponible.
    //     </p>
    //     {/* On pourrait quand même afficher la carte/carrousel sans tri */}
    //   </div>
    // );
  }

  if (loading && !userLocation && !locationError) return <div className="loading-container">Chargement des boutiques et obtention de votre position...</div>;
  if (loading) return <div className="loading-container">Chargement des boutiques...</div>;


  return (
    // Ajout d'un conteneur pour gérer le padding-bottom potentiel nécessaire pour le carrousel
    <div className="shops-page-container"> 
      {/* Header retiré car géré globalement dans AppContent */}
      <ShopsMap 
        shops={shops}
        // selectedShop={selectedShop} // On utilise activeShopId maintenant pour la mise en évidence
        activeShopId={activeShopId} // ID de la boutique à mettre en évidence / centrer
        onShopSelect={handleShopSelect} // Pour le clic sur marqueur -> ouvrir modale + synchro
        ref={mapRef} // Gardé si besoin futur d'interactions directes
      />

      {/* Le carrousel s'affiche toujours (sauf si pas de shops) */}
      <ShopCarousel
        shops={shops}
        onSlideChange={handleCarouselSlideChange} // Pour synchro carte
        onCardClick={handleCardClick} // Pour synchro carte au clic carte
        onInfoClick={handleShopSelect} // Pour ouvrir modal au clic sur info
        // Assurer que initialShopId se base sur la liste `shops` potentiellement triée
        initialShopId={activeShopId || (shops.length > 0 ? shops[0].id : null)} 
      />

      {/* La modale s'affiche seulement si un shop est sélectionné pour la modale */}
      {selectedShop && (
        <ShopModal
          shop={selectedShop}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Shops; 